import { useState, type ReactElement, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CONFIDENCE_SCALE_HINT,
  SESSION_ID_AGENT_HINT,
  SESSION_ID_HINT,
  SESSION_ID_TRANSCRIPT_HINT,
  hasSecretDetails,
  ruleRowLabel,
  sessionTranscriptLocation,
  sessionTranscriptLocationAlt,
  shouldShowRuleName,
  type AlertSecretDetailsData,
} from './AlertSecretDetails.helpers';
import { ConfidenceScaleExplanation } from './SecretConfidenceBadge';

interface AlertSecretDetailsProps {
  details: AlertSecretDetailsData;
  /**
   * Organization id from the route, used to build the single-project deep
   * link. When absent (e.g. account-scope views) the project renders as plain
   * text instead of a link.
   */
  organizationId?: string;
  className?: string;
}

const REPRESENTATIVE_HINT =
  'These values reflect the most recent occurrence in this group, not every occurrence.';

/** Human-readable label for the detection engine value. */
function engineLabel(engine: string): string {
  switch (engine) {
    case 'gitleaks':
      return 'Gitleaks';
    case 'regex':
      return 'Regex';
    case 'file-pattern':
      return 'File pattern';
    default:
      return engine;
  }
}

/**
 * A small info (ⓘ) icon with a hover/focus tooltip. `label` is the plain-text
 * accessible label; `content` optionally overrides the visible tooltip body with
 * richer markup — the string still backs the `aria-label` for screen readers.
 * Reused for the detail-row hints and the inline Session ID hint.
 */
function InfoHint({
  label,
  content,
}: {
  label: string;
  content?: ReactNode;
}): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          role="img"
          aria-label={label}
          className="inline-flex shrink-0 text-muted-foreground align-middle hover:text-foreground"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{content ?? label}</TooltipContent>
    </Tooltip>
  );
}

function DetailRow({
  label,
  hint,
  hintContent,
  children,
}: {
  label: string;
  /**
   * Optional explanation surfaced via a hover tooltip on an info icon to the
   * RIGHT of the value (mirrors the panel's representative hint). `hint` is the
   * plain-text accessible label; `hintContent` optionally overrides the visible
   * tooltip body with richer markup (e.g. a bulleted range) — the string still
   * backs the `aria-label` for screen readers.
   */
  hint?: string;
  hintContent?: ReactNode;
  children: ReactElement | string;
}): ReactElement {
  return (
    <div className="flex items-start gap-2 py-1">
      <div className="flex min-h-6 w-36 shrink-0 items-center text-xs text-muted-foreground">
        {label}
      </div>
      <div className="flex min-h-6 min-w-0 flex-1 items-center gap-1.5 font-mono text-xs break-all">
        {children}
        {hint && <InfoHint label={hint} content={hintContent} />}
      </div>
    </div>
  );
}

const TOOL_HINT =
  'The client tool whose input or output contained the secret — e.g. Read, Write, Edit, Bash, or an MCP tool. Absent for secrets typed directly into a chat prompt.';

const CONTEXT_HINT =
  'A short snippet of the scanned content around the secret. If the secret appears more than once, this shows the first occurrence.';

/**
 * Collapsible "More details" panel for secret alerts
 * (KIR-3765). Renders detection engine, rule, entropy, tool, session, and
 * project. Project precedence: a resolved git-remote `project` links to its
 * single-project page; otherwise the full `workspaceName` path shows as plain
 * text (with a copy button); otherwise the row is omitted.
 */
export function AlertSecretDetails({
  details,
  organizationId,
  className,
}: AlertSecretDetailsProps): ReactElement | null {
  const [open, setOpen] = useState(false);

  if (!hasSecretDetails(details)) {
    return null;
  }

  const {
    engine,
    ruleId,
    ruleName,
    entropy,
    toolName,
    sessionId,
    project,
    workspaceName,
    matchContext,
    platform,
  } = details;

  const showRuleName = shouldShowRuleName(engine, ruleName);

  return (
    <div className={cn('mt-3 border-t pt-3', className)}>
      <div className="flex items-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-1 text-xs"
          aria-expanded={open}
          onClick={() => setOpen(prev => !prev)}
        >
          {open ? (
            <ChevronDown className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden />
          )}
          More details
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              role="img"
              aria-label={REPRESENTATIVE_HINT}
              className="ml-1 inline-flex shrink-0 text-muted-foreground hover:text-foreground"
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {REPRESENTATIVE_HINT}
          </TooltipContent>
        </Tooltip>
      </div>

      {open && (
        <div className="mt-1 min-w-0">
          {engine && (
            <DetailRow label="Detection engine">
              {engineLabel(engine)}
            </DetailRow>
          )}
          {(ruleId || showRuleName) && (
            <DetailRow label={ruleRowLabel(engine)}>
              {showRuleName && ruleName ? (
                // Kirin rule: the readable description, then the raw pattern
                // (ruleId), each prefixed with a small word label so it is clear
                // which is which.
                <span className="inline-flex min-w-0 flex-col gap-0.5">
                  <span className="flex min-w-0 gap-1.5">
                    <span className="shrink-0 font-sans text-muted-foreground">
                      Description:
                    </span>
                    <span className="font-sans break-all">{ruleName}</span>
                  </span>
                  {ruleId && (
                    <span className="flex min-w-0 gap-1.5">
                      <span className="shrink-0 font-sans text-muted-foreground">
                        Pattern:
                      </span>
                      <span className="break-all">{ruleId}</span>
                    </span>
                  )}
                </span>
              ) : (
                (ruleId ?? ruleName ?? '')
              )}
            </DetailRow>
          )}
          {matchContext && (
            <div className="flex flex-col gap-1 py-1">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                Context
                <InfoHint label={CONTEXT_HINT} />
              </div>
              {/* Reuses the PII redacted-sample styling from AlertSourceDetails
                  so both "redacted excerpt" panels look identical. The snippet is
                  already secret- and PII-masked by the backend — rendered
                  verbatim (no token bolding in v1, KIR-3765). */}
              <pre className="max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs break-words whitespace-pre-wrap dark:bg-slate-700">
                {matchContext}
              </pre>
            </div>
          )}
          {typeof entropy === 'number' && (
            <DetailRow
              label="Entropy"
              hint={CONFIDENCE_SCALE_HINT}
              hintContent={<ConfidenceScaleExplanation />}
            >
              {entropy.toFixed(2)}
            </DetailRow>
          )}
          {toolName && (
            <DetailRow label="Tool" hint={TOOL_HINT}>
              {toolName}
            </DetailRow>
          )}
          {sessionId &&
            (() => {
              const location = sessionTranscriptLocation(platform, sessionId);
              const altLocation = sessionTranscriptLocationAlt(
                platform,
                sessionId
              );
              // Built twice — once flat for the accessible label, once as
              // <p> elements in the popover body below. Keep them in step.
              const hintLabel = [
                SESSION_ID_HINT,
                location ? SESSION_ID_TRANSCRIPT_HINT : null,
                location ? `Location: ${location}` : null,
                altLocation ? `${altLocation.when}: ${altLocation.path}` : null,
                SESSION_ID_AGENT_HINT,
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <DetailRow label="Session ID">
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <span className="break-all">{sessionId}</span>
                    <InfoHint
                      label={hintLabel}
                      content={
                        <div className="space-y-1">
                          <p>{SESSION_ID_HINT}</p>
                          {location && <p>{SESSION_ID_TRANSCRIPT_HINT}</p>}
                          {location && (
                            <p>
                              Location:{' '}
                              <code className="font-mono break-all">
                                {location}
                              </code>
                            </p>
                          )}
                          {altLocation && (
                            <p>
                              {altLocation.when}:{' '}
                              <code className="font-mono break-all">
                                {altLocation.path}
                              </code>
                            </p>
                          )}
                          <p>{SESSION_ID_AGENT_HINT}</p>
                        </div>
                      }
                    />
                    <CopyToClipboardButton
                      text={sessionId}
                      variant="ghost"
                      compactCopiedState
                      className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                    >
                      <span className="sr-only">Copy session ID</span>
                    </CopyToClipboardButton>
                  </span>
                </DetailRow>
              );
            })()}
          {project ? (
            <DetailRow label="Project">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                {organizationId ? (
                  <Link
                    to={`/${organizationId}/inventory/projects/${encodeURIComponent(project.slug)}`}
                    className="break-all text-primary hover:underline"
                  >
                    {project.name}
                  </Link>
                ) : (
                  <span className="break-all">{project.name}</span>
                )}
                <CopyToClipboardButton
                  text={project.name}
                  variant="ghost"
                  compactCopiedState
                  className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                >
                  <span className="sr-only">Copy project name</span>
                </CopyToClipboardButton>
              </span>
            </DetailRow>
          ) : workspaceName ? (
            // Local-only repo (no git remote): show the full workspace path
            // with a copy button (paths are long and worth copying).
            <DetailRow label="Workspace">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <span className="break-all">{workspaceName}</span>
                <CopyToClipboardButton
                  text={workspaceName}
                  variant="ghost"
                  compactCopiedState
                  className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                >
                  <span className="sr-only">Copy workspace path</span>
                </CopyToClipboardButton>
              </span>
            </DetailRow>
          ) : null}
        </div>
      )}
    </div>
  );
}

AlertSecretDetails.displayName = 'AlertSecretDetails';
